import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { BsHeadset, BsPlusCircle, BsPaperclip } from 'react-icons/bs';
import { useCustomer } from './CustomerContext';
import StatusBadge from './components/StatusBadge';
import EmptyState from './components/EmptyState';
import { apiFetch, parseJsonSafe, formatDate } from '../../utils/api';

const CATEGORY_OPTIONS = [
  { value: 'general', label: 'General' },
  { value: 'incidencia', label: 'Incidencia técnica' },
  { value: 'consulta', label: 'Consulta' },
  { value: 'facturacion', label: 'Facturación' },
  { value: 'otro', label: 'Otro' }
];

const CustomerSupportPage = () => {
  const { authHeaders, logout } = useCustomer();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ subject: '', category: 'general', body: '' });
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [formError, setFormError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiFetch('/api/v1/software/customer/portal/tickets', { headers: authHeaders() });
      if (res.status === 401) { logout(); return; }
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error((data && data.message) || 'No se pudieron cargar los tickets');
      setTickets(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [authHeaders, logout]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.subject.trim()) { setFormError('Indica un asunto'); return; }
    if (!form.body.trim()) { setFormError('Describe tu consulta'); return; }
    setSending(true);
    setFormError('');
    try {
      const res = await apiFetch('/api/v1/software/customer/portal/tickets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(form)
      });
      if (res.status === 401) { logout(); return; }
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error((data && data.message) || 'No se pudo crear el ticket');
      const failed = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        const up = await apiFetch(`/api/v1/software/customer/portal/tickets/${data.id}/attachments`, { method: 'POST', headers: authHeaders(), body: fd });
        if (!up.ok) failed.push(file.name);
      }
      if (failed.length) window.alert(`El ticket se creó, pero no se pudieron subir estos archivos: ${failed.join(', ')}. Puedes volver a adjuntarlos desde el ticket.`);
      navigate(`/cliente/soporte/${data.id}`);
    } catch (e) {
      setFormError(e.message);
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div>
        <div className="cp-skeleton cp-skeleton-title" />
        <div className="cp-skeleton cp-skeleton-card" style={{ height: 260 }} />
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 6 }}>
        <h1 className="cp-page-title" style={{ margin: 0 }}>Soporte</h1>
        {!showForm && (
          <button className="cp-btn cp-btn-primary" onClick={() => { setShowForm(true); setFormError(''); }}>
            <BsPlusCircle /> Nuevo ticket
          </button>
        )}
      </div>
      <p style={{ color: 'var(--cp-muted)', marginTop: 0, marginBottom: 18 }}>
        Crea un ticket para contactar con nuestro equipo. Te avisaremos por email cuando te respondamos.
      </p>

      {showForm && (
        <div className="cp-card-static" style={{ padding: 20, marginBottom: 22 }}>
          <h2 className="cp-page-title" style={{ fontSize: 18, marginTop: 0, marginBottom: 14 }}>Nuevo ticket</h2>
          {formError && <div className="cp-alert cp-alert-error" style={{ marginBottom: 12 }}>{formError}</div>}
          <div style={{ marginBottom: 12 }}>
            <label className="cp-label">Asunto</label>
            <input className="cp-input" value={form.subject} onChange={(e) => setForm((p) => ({ ...p, subject: e.target.value }))} placeholder="Resumen breve" />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label className="cp-label">Categoría</label>
            <select className="cp-input" value={form.category} onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}>
              {CATEGORY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label className="cp-label">¿En qué podemos ayudarte?</label>
            <textarea className="cp-input" rows="5" value={form.body} onChange={(e) => setForm((p) => ({ ...p, body: e.target.value }))} placeholder="Cuéntanos con detalle tu incidencia o consulta…" />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label className="cp-btn cp-btn-outline cp-btn-sm" style={{ cursor: 'pointer' }}>
              <BsPaperclip /> Adjuntar archivos
              <input type="file" multiple hidden onChange={(e) => setFiles((prev) => [...prev, ...Array.from(e.target.files)])} />
            </label>
            {files.length > 0 && (
              <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {files.map((f, i) => (
                  <span key={i} className="cp-badge cp-badge-draft" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    {f.name}
                    <button type="button" onClick={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'inherit' }}>×</button>
                  </span>
                ))}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="cp-btn cp-btn-outline" onClick={() => { setShowForm(false); setFiles([]); setForm({ subject: '', category: 'general', body: '' }); }} disabled={sending}>Cancelar</button>
            <button className="cp-btn cp-btn-primary" onClick={submit} disabled={sending}>{sending ? 'Enviando…' : 'Enviar ticket'}</button>
          </div>
        </div>
      )}

      {error && <div className="cp-alert cp-alert-error" style={{ marginBottom: 14 }}>{error}</div>}

      {!tickets.length ? (
        !showForm && <EmptyState icon={BsHeadset} title="Sin tickets" subtitle="Aún no has creado ningún ticket de soporte." actionLabel="Crear ticket" onAction={() => setShowForm(true)} />
      ) : (
        <div className="cp-card-static">
          <div style={{ overflowX: 'auto' }}>
            <table className="cp-table">
              <thead>
                <tr>
                  <th>Asunto</th>
                  <th>Estado</th>
                  <th>Mensajes</th>
                  <th>Última actividad</th>
                </tr>
              </thead>
              <tbody>
                {tickets.map((t) => (
                  <tr key={t.id}>
                    <td style={{ fontWeight: 500 }}>
                      <Link to={`/cliente/soporte/${t.id}`} style={{ color: 'var(--cp-accent)', textDecoration: 'none', fontWeight: 600 }}>
                        {t.client_unread && <span title="Nueva respuesta" style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#dc3545', marginRight: 6 }} />}
                        #{t.id} · {t.subject}
                      </Link>
                    </td>
                    <td><StatusBadge status={t.status} /></td>
                    <td>{t.message_count}</td>
                    <td>{formatDate(t.last_message_at || t.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerSupportPage;
